import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { SuccessNotify, FailedNotify } from "../../constants/tostify";
import {
  useSendConnectionRequestMutation,
  useGetAllConnectedQuery,
} from "../../features/connection/connectionApiSlice";
import { useGetAllShopsByUserIdQuery } from "../../features/shop/shopApiSlice";
import Subscriptionmdl from "../../components/subscription_modal/index";
import {
  getAllowedConnectTypesForTarget,
  isBusinessTypeInAllowlist,
  getBusinessTypeLabel,
  getConnectionRestrictionMessage,
  getConnectionAllowlistHint,
} from "../../constants/businessTypeConnectionRules";

const ConnectProfileModal = ({
  connectbuyModal,
  setConnectbuyModal,
  shopData,
  /** Optional explicit allowlist; defaults from target shop businessType */
  allowedConnectBusinessTypes,
}) => {
  const { name } = useSelector((state) => state.user);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    if (connectbuyModal) {
      setSelectedProfile(null);
      setSearch("");
      setOpenDropdown(false);
    }
  }, [connectbuyModal, shopData?._id]);

  // Subscription modal state
  const [lgShow, setLgShow] = useState(false);
  const [purchase, setPurchase] = useState(false);

  /* ================= FETCH SHOPS via RTK Query ================= */
  const { data: shopsResponse } = useGetAllShopsByUserIdQuery(name?._id, {
    skip: !connectbuyModal || !name?._id,
  });
  const shops = shopsResponse?.data || [];

  const allowlist = useMemo(() => {
    if (Array.isArray(allowedConnectBusinessTypes) && allowedConnectBusinessTypes.length) {
      return allowedConnectBusinessTypes;
    }
    return getAllowedConnectTypesForTarget(shopData?.businessType);
  }, [allowedConnectBusinessTypes, shopData?.businessType]);

  const shopsWithGeneral = useMemo(() => {
    const targetShopId = shopData?._id ? String(shopData._id) : "";
    const targetOwnerId = String(
      shopData?.userid?._id || shopData?.userid || ""
    );
    const myId = name?._id ? String(name._id) : "";

    // Never offer the target shop itself as a sender profile
    const excludeSelfShop = (item) => {
      const itemId = item?.id || item?._id;
      if (targetShopId && itemId && String(itemId) === targetShopId) return false;
      return true;
    };

    // If connecting to your own shop, do not offer any sender profiles
    if (myId && targetOwnerId && myId === targetOwnerId) {
      return [];
    }

    // When target has a shop-type allowlist, only show those shops/profiles.
    // Include General Public when it is explicitly allowed (e.g. Retailer).
    if (allowlist) {
      const filteredShops = (shops || []).filter(
        (item) =>
          excludeSelfShop(item) &&
          isBusinessTypeInAllowlist(item?.businessType, allowlist)
      );

      if (isBusinessTypeInAllowlist("generalPublic", allowlist) && myId) {
        const generalProfile = {
          userid: name?._id,
          id: name?._id,
          name: name?.fullname || "General Profile",
          businessType: "generalPublic",
        };
        return [generalProfile, ...filteredShops];
      }

      return filteredShops;
    }

    const generalProfile = {
      userid: name?._id,
      id: name?._id,
      name: name?.fullname || "General Profile",
      businessType: "generalPublic",
    };
    return [generalProfile, ...(shops || []).filter(excludeSelfShop)];
  }, [allowlist, shops, name?._id, name?.fullname, shopData?._id, shopData?.userid]);

  const getTitle = (item) =>
    item?.name || item?.shopname || item?.companyname || "Unnamed Shop";

  // Search filter
  const filteredShops = shopsWithGeneral.filter((item) => {
    const title = getTitle(item).toLowerCase();
    const label = getBusinessTypeLabel(item?.businessType).toLowerCase();
    const bt = String(item?.businessType || "").toLowerCase();
    const q = search.toLowerCase();
    return title.includes(q) || label.includes(q) || bt.includes(q);
  });

  const { data: connectedDataResponse } = useGetAllConnectedQuery(
    { userid: name?._id },
    { skip: !connectbuyModal || !name?._id }
  );

  const getProfileShopKey = (profile) =>
    profile?.businessType === "generalPublic" ? null : profile?.id;

  const getProfileStatus = (profile) => {
    const targetId = shopData?._id ? String(shopData._id) : null;
    if (!targetId) return null;
    const key = getProfileShopKey(profile);
    const keyStr = key ? String(key) : null;
    const matches = (entry) => {
      const entrySender = entry?.sendershopid ? String(entry.sendershopid) : null;
      return (
        entrySender === keyStr &&
        String(entry?.recievershopid || "") === targetId
      );
    };
    if ((connectedDataResponse?.connected || []).some(matches)) return "connected";
    if ((connectedDataResponse?.requested || []).some(matches)) return "requested";
    return null;
  };

  const [sendConnectionRequest] = useSendConnectionRequestMutation();

  /* ================= CONNECT API ================= */
  const ConnectShop = async (profile) => {
    if (!profile) {
      FailedNotify("Please select a shop first!");
      return;
    }

    const myId = name?._id ? String(name._id) : "";
    const targetOwnerId = String(
      shopData?.userid?._id || shopData?.userid || ""
    );
    if (myId && targetOwnerId && myId === targetOwnerId) {
      FailedNotify("You cannot connect to your own shop");
      return;
    }

    const targetShopId = shopData?._id ? String(shopData._id) : "";
    const senderShopId = profile?.id || profile?._id;
    if (
      targetShopId &&
      senderShopId &&
      String(senderShopId) === targetShopId
    ) {
      FailedNotify("You cannot connect a shop to itself");
      return;
    }

    if (allowlist && !isBusinessTypeInAllowlist(profile.businessType, allowlist)) {
      FailedNotify(
        getConnectionRestrictionMessage(shopData?.businessType)
      );
      return;
    }

    const senderid = profile.userid;
    const sendershopid = profile.id;
    const senderBusinessType = profile.businessType;

    // Never fall back to shop _id as recieverid — that keyed Connection docs
    // under the shop id, so the owner's Received inbox (userid = owner) looked empty.
    const receiverUserId =
      shopData?.userid?._id || shopData?.userid || null;
    if (!receiverUserId) {
      FailedNotify("Unable to resolve shop owner for connection request.");
      return;
    }

    const requestBody = {
      senderid,
      ...(senderBusinessType !== "generalPublic" ? { sendershopid } : {}),
      senderBusinessType,
      businessType: shopData?.businessType,
      recieverid: receiverUserId,
      recievershopid: shopData?._id,
    };

    try {
      await sendConnectionRequest(requestBody).unwrap();
      SuccessNotify("Connection request sent successfully!");
      setConnectbuyModal(false);
    } catch (error) {
      const errMsg =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "Failed to send connection request.";
      if (errMsg.toLowerCase().includes("limit exceeded")) {
        FailedNotify(errMsg);
        setLgShow(true);
        setConnectbuyModal(false);
      } else {
        FailedNotify(errMsg);
      }
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <Subscriptionmdl
        lgShow={lgShow}
        setLgShow={setLgShow}
        setPurchase={setPurchase}
        purchase={purchase}
      />
      <Modal
        show={connectbuyModal}
        onHide={() => setConnectbuyModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Shop</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="fw-semibold mb-2">
            Select your shop to connect
          </p>

          {/* Fake Select Box */}
          <div
            className="border rounded p-2 d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => setOpenDropdown(!openDropdown)}
          >
            <span>
              {selectedProfile ? getTitle(selectedProfile) : "Select Shop"}
            </span>
            <span>▾</span>
          </div>

          {/* Dropdown Panel */}
          {openDropdown && (
            <div
              className="border rounded mt-2 p-2"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                background: "#fff",
              }}
            >
              {/* Search Bar */}
              <Form.Control
                type="text"
                placeholder="Search shop..."
                className="mb-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {/* Shops List */}
              {filteredShops.map((item, index) => {
                const status = getProfileStatus(item);
                const isDisabled =
                  status === "connected" || status === "requested";
                return (
                  <div
                    key={item?.id || item?._id || index}
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedProfile(item);
                      setOpenDropdown(false);
                    }}
                    className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.6 : 1,
                      backgroundColor: isDisabled ? "#f8f9fa" : undefined,
                    }}
                  >
                    <div>
                      <div className="fw-bold">{getTitle(item)}</div>
                      <small className="text-muted">
                        {getBusinessTypeLabel(item?.businessType) ||
                          item?.businessType ||
                          ""}
                      </small>
                    </div>

                    {status && (
                      <small
                        className="fw-semibold"
                        style={{
                          color:
                            status === "connected" ? "#155724" : "#856404",
                        }}
                      >
                        {status === "connected" ? "Connected" : "Requested"}
                      </small>
                    )}
                  </div>
                );
              })}

              {filteredShops.length === 0 && (
                <div className="text-center text-muted py-2">
                  No shops found
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="w-100"
            onClick={() => ConnectShop(selectedProfile)}
          >
            Connect
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ConnectProfileModal;
